
import React, { useState, useEffect, useCallback, useRef } from "react";
import { safeFormat } from '@/lib/safeDate';
import { useTranslation } from 'react-i18next';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowLeft, Save, Mail, Calendar, Phone, User as UserIcon, Briefcase, Building2, Users, Eye, Camera, Loader2, X, DollarSign, ExternalLink, Flag } from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useGoBack } from "@/hooks/useGoBack";
import { createPageUrl } from "@/utils";
import { format } from "date-fns";
import { toast } from "sonner";
import { useAppUser, useAppUsers } from "@/hooks/useAppUsers";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useTwilioPhoneNumbers } from "@/hooks/useTwilioPhoneNumbers";
import { useSystemRoles } from "@/hooks/useSystemRoles";
import { useDepartments } from "@/hooks/useDepartments";
import { getAvatarColor } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";
import { Checkbox } from "@/components/ui/checkbox";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { useEmployeeByAppUserId } from "@/hooks/finance/useEmployees";
import { DeactivateUserModal } from "@/components/settings/DeactivateUserModal";

export default function UserDetail() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const goBack = useGoBack('/admin');
  const [searchParams] = useSearchParams();
  const userId = searchParams.get('id');
  const { user, loading, loadUser } = useAppUser(userId);
  const { data: linkedEmployee, isLoading: loadingEmployee } = useEmployeeByAppUserId(userId);
  const { updateUser, users: allUsers, loadUsers } = useAppUsers();
  const { phoneNumbers, loading: loadingPhoneNumbers } = useTwilioPhoneNumbers(userId);
  const { roles, isLoading: loadingRoles } = useSystemRoles();
  const { departments, isLoading: loadingDepartments } = useDepartments();
  const [isSaving, setIsSaving] = useState(false);
  const [isUploadingAvatar, setIsUploadingAvatar] = useState(false);
  const [avatarUrl, setAvatarUrl] = useState(null);
  const avatarInputRef = useRef(null);
  const [deactivateModal, setDeactivateModal] = useState({ open: false, dealCount: 0 });
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    display_name: "",
    email: "",
    twilio_phone_number: "",
    active: true,
    is_outbound_sdr: false,
    system_role_id: "",
    department_id: "",
    manager_id: "",
    accessible_department_ids: [],
    primary_business_unit: ""
  });

  useEffect(() => {
    if (!userId) {
      navigate(createPageUrl("UserManagement"));
      return;
    }
    loadUsers(); // Load all users for manager selection
  }, [userId, navigate]);

  useEffect(() => {
    if (user) {
      const phoneValue = user.twilio_phone_number || "none";
      const accessibleDeptIds = user.accessible_departments?.map((ad) => ad.department.id) || [];
      
      setFormData({
        first_name: user.first_name || "",
        last_name: user.last_name || "",
        display_name: user.display_name || "",
        email: user.email || "",
        twilio_phone_number: phoneValue,
        active: user.active ?? true,
        is_outbound_sdr: user.is_outbound_sdr ?? false,
        system_role_id: user.system_role_id || "",
        department_id: user.department_id || "",
        manager_id: user.manager_id || "",
        accessible_department_ids: accessibleDeptIds,
        primary_business_unit: user.primary_business_unit || ""
      });
      
      setAvatarUrl(user.avatar_url || null);
    }
  }, [user]);

  // Handle avatar upload
  const handleAvatarUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !userId) return;

    // Validate file type
    const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      toast.error('Tipo de arquivo inválido. Use JPG, PNG, GIF ou WebP.');
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Arquivo muito grande. Máximo 5MB.');
      return;
    }

    setIsUploadingAvatar(true);
    try {
      // Generate unique filename
      const fileExt = file.name.split('.').pop();
      const fileName = `${userId}-${Date.now()}.${fileExt}`;
      const filePath = `${fileName}`;

      // Delete old avatar if exists
      if (avatarUrl) {
        const oldPath = avatarUrl.split('/avatars/')[1];
        if (oldPath) {
          await supabase.storage.from('avatars').remove([oldPath]);
        }
      }

      // Upload new avatar
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('avatars')
        .upload(filePath, file, {
          contentType: file.type,
          upsert: true
        });

      if (uploadError) throw uploadError;

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('avatars')
        .getPublicUrl(filePath);

      // Update user record
      const { error: updateError } = await supabase
        .from('app_users')
        .update({ avatar_url: publicUrl })
        .eq('id', userId);

      if (updateError) throw updateError;

      setAvatarUrl(publicUrl);
      toast.success('Foto atualizada com sucesso!');
    } catch (error) {
      console.error('Error uploading avatar:', error);
      toast.error('Erro ao fazer upload da foto.');
    } finally {
      setIsUploadingAvatar(false);
    }
  };

  const handleRemoveAvatar = async () => {
    if (!avatarUrl || !userId) return;

    setIsUploadingAvatar(true);
    try {
      // Delete from storage
      const oldPath = avatarUrl.split('/avatars/')[1];
      if (oldPath) {
        await supabase.storage.from('avatars').remove([oldPath]);
      }

      // Update user record
      const { error: updateError } = await supabase
        .from('app_users')
        .update({ avatar_url: null })
        .eq('id', userId);

      if (updateError) throw updateError;

      setAvatarUrl(null);
      toast.success('Foto removida com sucesso!');
    } catch (error) {
      console.error('Error removing avatar:', error);
      toast.error('Erro ao remover foto.');
    } finally {
      setIsUploadingAvatar(false);
    }
  };

  // Valida e sincroniza o número do telefone quando os phoneNumbers são carregados
  useEffect(() => {
    if (!loadingPhoneNumbers && phoneNumbers.length > 0 && user) {
      const currentValue = user.twilio_phone_number || "none";
      const numberExists = phoneNumbers.some(option => option.value === currentValue);
      
      if (!numberExists && currentValue !== "none") {
        console.warn(`Phone number ${currentValue} not found in Twilio numbers, resetting to none`);
        setFormData(prev => ({ ...prev, twilio_phone_number: "none" }));
      } else {
        setFormData(prev => ({ ...prev, twilio_phone_number: currentValue }));
      }
    }
  }, [loadingPhoneNumbers, phoneNumbers, user]);

  // Salva todos os campos do form, exceto opcionalmente o flag `active`
  // (usado para evitar que o save normal flipe `active` quando estamos delegando essa parte
  // para a RPC de redistribuição).
  const persistFormData = async ({ skipActiveFlag = false } = {}) => {
    const phoneNumber = formData.twilio_phone_number === 'none' ? null : formData.twilio_phone_number;

    const payload = {
      first_name: formData.first_name,
      last_name: formData.last_name,
      display_name: formData.display_name,
      twilio_phone_number: phoneNumber,
      is_outbound_sdr: formData.is_outbound_sdr,
      system_role_id: formData.system_role_id || null,
      department_id: formData.department_id || null,
      manager_id: formData.manager_id || null,
      primary_business_unit: formData.primary_business_unit || null,
    };
    if (!skipActiveFlag) {
      payload.active = formData.active;
    }

    await updateUser(userId, payload);

    // Update accessible departments
    if (formData.accessible_department_ids.length > 0) {
      await supabase
        .from('user_department_access')
        .delete()
        .eq('user_id', userId);

      const accessRecords = formData.accessible_department_ids.map(deptId => ({
        user_id: userId,
        department_id: deptId
      }));

      const { error: accessError } = await supabase
        .from('user_department_access')
        .insert(accessRecords);

      if (accessError) throw accessError;
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!userId) return;

    // Detecta transição active=true -> active=false
    const isDeactivating = user?.active === true && formData.active === false;

    if (isDeactivating) {
      try {
        const { data: dealCount, error: countError } = await supabase.rpc(
          'count_user_active_deals_as_consultant',
          { p_user_id: userId }
        );
        if (countError) throw countError;

        if ((dealCount ?? 0) > 0) {
          // Abre modal de redistribuição; ele cuida do flip de active via RPC
          setDeactivateModal({ open: true, dealCount: dealCount });
          return;
        }
      } catch (error) {
        console.error('Erro ao verificar deals do usuário:', error);
        toast.error('Erro ao verificar deals ativos do usuário.');
        return;
      }
    }

    setIsSaving(true);
    try {
      await persistFormData();
      toast.success(t('userDetail.updateSuccess'));
      navigate(-1);
    } catch (error) {
      console.error("Error updating user:", error);
      toast.error(t('userDetail.updateError'));
    } finally {
      setIsSaving(false);
    }
  };

  // Após confirmação do modal: deals já foram redistribuídos e active=false já foi setado
  // pela RPC. Persistimos o resto do form sem mexer no active e voltamos.
  const handleDeactivationConfirmed = async () => {
    setIsSaving(true);
    try {
      await persistFormData({ skipActiveFlag: true });
      await loadUser?.();
      navigate(-1);
    } catch (error) {
      console.error("Error updating user after deactivation:", error);
      toast.error(t('userDetail.updateError'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const toggleDepartmentAccess = (deptId) => {
    setFormData(prev => {
      const currentIds = prev.accessible_department_ids;
      const newIds = currentIds.includes(deptId)
        ? currentIds.filter(id => id !== deptId)
        : [...currentIds, deptId];
      return { ...prev, accessible_department_ids: newIds };
    });
  };

  if (loading) {
    return (
      <div className="bg-slate-50 p-6 md:p-8">
        <div className="max-w-5xl mx-auto">
          <div className="animate-pulse space-y-6">
            <div className="h-8 bg-slate-200 rounded w-1/4"></div>
            <div className="h-64 bg-slate-200 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="bg-slate-50 p-6 md:p-8">
        <div className="max-w-5xl mx-auto">
          <p className="text-slate-600">{t('userDetail.userNotFound')}</p>
        </div>
      </div>
    );
  }

  const getUserDisplayName = () => {
    if (user.display_name) return user.display_name;
    const nameParts = [user.first_name, user.last_name].filter(Boolean);
    return nameParts.length > 0 ? nameParts.join(' ') : t('userDetail.defaultUser');
  };

  const getUserInitial = () => {
    if (user.first_name) return user.first_name.charAt(0).toUpperCase();
    if (user.display_name) return user.display_name.charAt(0).toUpperCase();
    return 'U';
  };

  // Filter potential managers (only Sales Managers and Head of Operations)
  const potentialManagers = allUsers.filter(u => 
    u.id !== userId && 
    u.system_role?.code && 
    ['sales_manager', 'head_operations'].includes(u.system_role.code)
  );

  return (
    <div className="bg-slate-50 p-6 md:p-8">
      <div className="max-w-5xl mx-auto">
        <button
          onClick={goBack}
          className="mb-6 text-slate-600 flex items-center gap-2 hover:scale-105 transition-transform duration-200"
        >
          <ArrowLeft className="w-4 h-4" />
          {t('common.back')}
        </button>

        <div className="mb-8">
          <div className="flex items-center gap-6 mb-2">
            {/* Avatar with Upload */}
            <div className="relative group">
              <Avatar className="w-20 h-20 border-4 border-white shadow-xl">
                <AvatarImage src={avatarUrl || undefined} alt={getUserDisplayName()} />
                <AvatarFallback className={`text-2xl font-bold text-white bg-gradient-to-br ${getAvatarColor(user?.first_name || user?.display_name)}`}>
                  {getUserInitial()}
                </AvatarFallback>
              </Avatar>
              
              {/* Upload Overlay */}
              <div 
                className="absolute inset-0 bg-black/50 rounded-full opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer flex items-center justify-center"
                onClick={() => !isUploadingAvatar && avatarInputRef.current?.click()}
              >
                {isUploadingAvatar ? (
                  <Loader2 className="w-6 h-6 text-white animate-spin" />
                ) : (
                  <Camera className="w-6 h-6 text-white" />
                )}
              </div>
              
              {/* Remove button */}
              {avatarUrl && !isUploadingAvatar && (
                <button
                  type="button"
                  onClick={handleRemoveAvatar}
                  className="absolute -top-1 -right-1 bg-red-500 hover:bg-red-600 text-white rounded-full p-1 shadow-lg opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <X className="w-3 h-3" />
                </button>
              )}
              
              {/* Hidden file input */}
              <input
                ref={avatarInputRef}
                type="file"
                accept="image/jpeg,image/png,image/gif,image/webp"
                onChange={handleAvatarUpload}
                className="hidden"
              />
            </div>
            
            <div>
              <h1 className="text-3xl font-bold text-slate-900">
                {getUserDisplayName()}
              </h1>
              <p className="text-sm text-slate-500 mt-1">
                Passe o mouse sobre a foto para alterar
              </p>
              <div className="flex items-center gap-2 mt-2">
                <Badge variant={user.active ? "default" : "destructive"}>
                  {user.active ? t('userDetail.active') : t('userDetail.inactive')}
                </Badge>
                {user.system_role && (
                  <Badge variant="outline" className="border-purple-200 text-purple-700">
                    {user.system_role.name}
                  </Badge>
                )}
              </div>
            </div>
          </div>
        </div>

        <form onSubmit={handleSave} className="space-y-6">
          {/* Basic Information */}
          <Card className="border-slate-200 shadow-sm">
            <CardHeader className="border-b border-slate-100">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <UserIcon className="w-5 h-5 text-blue-600" />
                </div>
                <CardTitle className="text-xl">{t('userDetail.userInformation')}</CardTitle>
              </div>
            </CardHeader>
            <CardContent className="p-6 space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="first_name">{t('userDetail.firstName')}</Label>
                  <Input
                    id="first_name"
                    value={formData.first_name}
                    onChange={(e) => handleChange('first_name', e.target.value)}
                    placeholder="John"
                    className="border-slate-300"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="last_name">{t('userDetail.lastName')}</Label>
                  <Input
                    id="last_name"
                    value={formData.last_name}
                    onChange={(e) => handleChange('last_name', e.target.value)}
                    placeholder="Doe"
                    className="border-slate-300"
                  />
                </div>

                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="display_name">{t('userDetail.displayName')}</Label>
                  <Input
                    id="display_name"
                    value={formData.display_name}
                    onChange={(e) => handleChange('display_name', e.target.value)}
                    placeholder="John Doe"
                    className="border-slate-300"
                  />
                  <p className="text-xs text-slate-500">
                    {t('userDetail.displayNameHelper')}
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email">{t('userDetail.email')}</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      disabled
                      className="pl-10 border-slate-300 bg-slate-50"
                    />
                  </div>
                  <p className="text-xs text-slate-500">{t('userDetail.emailCannotChange')}</p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="twilio_phone_number">{t('userDetail.twilioPhoneNumber')}</Label>
                  <Select
                    value={formData.twilio_phone_number}
                    onValueChange={(value) => handleChange('twilio_phone_number', value)}
                    disabled={loadingPhoneNumbers}
                  >
                    <SelectTrigger className="border-slate-300">
                      <Phone className="w-4 h-4 text-slate-400 mr-2" />
                      <SelectValue placeholder={loadingPhoneNumbers ? t('userDetail.loadingNumbers') : t('userDetail.selectNumber')} />
                    </SelectTrigger>
                    <SelectContent>
                      {phoneNumbers.map((option) => (
                        <SelectItem 
                          key={option.value} 
                          value={option.value}
                          disabled={option.disabled}
                          className={option.disabled ? "opacity-50 cursor-not-allowed" : ""}
                        >
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-slate-500">
                    {t('userDetail.twilioPhoneHelper')}
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="primary_business_unit">Unidade de Negócio</Label>
                  <Select
                    value={formData.primary_business_unit || "none"}
                    onValueChange={(value) => handleChange('primary_business_unit', value === 'none' ? '' : value)}
                  >
                    <SelectTrigger className="border-slate-300">
                      <Flag className="w-4 h-4 text-slate-400 mr-2" />
                      <SelectValue placeholder="Selecione a unidade" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Não definido</SelectItem>
                      <SelectItem value="us">🇺🇸 Estados Unidos (USD)</SelectItem>
                      <SelectItem value="br">🇧🇷 Brasil (BRL)</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-slate-500">
                    Define a qual unidade de negócio este usuário pertence para fins de relatórios
                  </p>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <Label htmlFor="active">{t('userDetail.activeStatus')}</Label>
                      <p className="text-xs text-slate-500">
                        {t('userDetail.activeStatusHelper')}
                      </p>
                    </div>
                    <Switch
                      id="active"
                      checked={formData.active}
                      onCheckedChange={(checked) => handleChange('active', checked)}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <Label htmlFor="is_outbound_sdr">SDR Outbound</Label>
                      <p className="text-xs text-slate-500">
                        Recebe fila automática de 150 contatos outbound por dia.
                      </p>
                    </div>
                    <Switch
                      id="is_outbound_sdr"
                      checked={formData.is_outbound_sdr}
                      onCheckedChange={(checked) => handleChange('is_outbound_sdr', checked)}
                    />
                  </div>
                </div>
              </div>

              {user.created_at && (
                <div className="pt-4 border-t border-slate-100">
                  <div className="flex items-center gap-2 text-sm text-slate-600">
                    <Calendar className="w-4 h-4" />
                    <span>{t('userDetail.joined')} {safeFormat(new Date(user.created_at), "MMMM d, yyyy 'at' h:mm a")}</span>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Financial/HR Data - Only shown if linked to employee */}
          {linkedEmployee && (
            <Card className="border-green-200 shadow-sm bg-green-50/30">
              <CardHeader className="border-b border-green-100">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-green-100 rounded-lg">
                      <DollarSign className="w-5 h-5 text-green-600" />
                    </div>
                    <CardTitle className="text-xl">Dados Financeiros/RH</CardTitle>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => navigate('/finance/settings/employees')}
                    className="border-green-300 text-green-700 hover:bg-green-100"
                  >
                    <ExternalLink className="w-4 h-4 mr-2" />
                    Ver ficha completa
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="p-6">
                <div className="grid md:grid-cols-3 gap-6">
                  <div className="space-y-1">
                    <Label className="text-xs text-slate-500">Cargo (RH)</Label>
                    <p className="font-medium">{linkedEmployee.role || '-'}</p>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-slate-500">Departamento (RH)</Label>
                    <p className="font-medium">{linkedEmployee.department || '-'}</p>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-slate-500">Business Unit</Label>
                    <p className="font-medium">
                      {linkedEmployee.business_unit === 'us' ? '🇺🇸 USA' : 
                       linkedEmployee.business_unit === 'br' ? '🇧🇷 Brasil' : '🌐 Compartilhado'}
                    </p>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-slate-500">Salário Base</Label>
                    <p className="font-medium text-green-600">
                      {new Intl.NumberFormat(linkedEmployee.salary_currency === 'BRL' ? 'pt-BR' : 'en-US', {
                        style: 'currency',
                        currency: linkedEmployee.salary_currency || 'USD',
                      }).format(linkedEmployee.base_salary || 0)}
                    </p>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-slate-500">Data de Contratação</Label>
                    <p className="font-medium">
                      {linkedEmployee.hire_date 
                        ? safeFormat(new Date(linkedEmployee.hire_date), "dd/MM/yyyy") 
                        : '-'}
                    </p>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-slate-500">Tipo de Custo</Label>
                    <p className="font-medium">
                      {linkedEmployee.cost_type === 'fixed' ? 'Fixo' : 
                       linkedEmployee.cost_type === 'variable' ? 'Variável' : 'Misto'}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Permissions & Access Control */}
          <Card className="border-slate-200 shadow-sm">
            <CardHeader className="border-b border-slate-100">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <Briefcase className="w-5 h-5 text-purple-600" />
                </div>
                <CardTitle className="text-xl">{t('userDetail.permissionsAccessControl')}</CardTitle>
              </div>
            </CardHeader>
            <CardContent className="p-6 space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="system_role_id">
                    {t('userDetail.role')} <span className="text-red-500">*</span>
                  </Label>
                  <Select
                    value={formData.system_role_id}
                    onValueChange={(value) => handleChange('system_role_id', value)}
                    disabled={loadingRoles}
                  >
                    <SelectTrigger className="border-slate-300">
                      <Briefcase className="w-4 h-4 text-slate-400 mr-2" />
                      <SelectValue placeholder={loadingRoles ? t('userDetail.loadingRoles') : t('userDetail.selectRole')} />
                    </SelectTrigger>
                    <SelectContent>
                      {roles.map((role) => (
                        <SelectItem key={role.id} value={role.id}>
                          {role.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-slate-500">
                    {t('userDetail.roleHelper')}
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="department_id">
                    {t('userDetail.department')} <span className="text-red-500">*</span>
                  </Label>
                  <Select
                    value={formData.department_id}
                    onValueChange={(value) => handleChange('department_id', value)}
                    disabled={loadingDepartments}
                  >
                    <SelectTrigger className="border-slate-300">
                      <Building2 className="w-4 h-4 text-slate-400 mr-2" />
                      <SelectValue placeholder={loadingDepartments ? t('userDetail.loadingDepartments') : t('userDetail.selectDepartment')} />
                    </SelectTrigger>
                    <SelectContent>
                      {departments.map((dept) => (
                        <SelectItem key={dept.id} value={dept.id}>
                          {dept.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-slate-500">
                    {t('userDetail.departmentHelper')}
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="manager_id">{t('userDetail.manager')}</Label>
                  <Select
                    value={formData.manager_id || 'none'}
                    onValueChange={(value) => handleChange('manager_id', value === 'none' ? '' : value)}
                  >
                    <SelectTrigger className="border-slate-300">
                      <Users className="w-4 h-4 text-slate-400 mr-2" />
                      <SelectValue placeholder={t('userDetail.selectManager')} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">{t('userDetail.noManager')}</SelectItem>
                      {potentialManagers.map((manager) => (
                        <SelectItem key={manager.id} value={manager.id}>
                          {manager.display_name || `${manager.first_name} ${manager.last_name}`}
                          {manager.department && ` (${manager.department.name})`}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-slate-500">
                    {t('userDetail.managerHelper')}
                  </p>
                </div>

                <div className="space-y-2 md:col-span-2">
                  <Label>
                    <Eye className="w-4 h-4 inline mr-2" />
                    {t('userDetail.canViewDepartments')}
                  </Label>
                  <p className="text-xs text-slate-500 mb-3">
                    {t('userDetail.canViewDepartmentsHelper')}
                  </p>
                  <div className="grid grid-cols-2 gap-3">
                    {departments.map((dept) => (
                      <div key={dept.id} className="flex items-center space-x-2">
                        <Checkbox
                          id={`dept-${dept.id}`}
                          checked={formData.accessible_department_ids.includes(dept.id)}
                          onCheckedChange={() => toggleDepartmentAccess(dept.id)}
                        />
                        <Label
                          htmlFor={`dept-${dept.id}`}
                          className="text-sm font-normal cursor-pointer"
                        >
                          {dept.name}
                        </Label>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Save Button */}
          <div className="flex justify-end gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate(createPageUrl("UserManagement"))}
              disabled={isSaving}
              className="border-slate-300"
            >
              {t('common.cancel')}
            </Button>
            <Button
              type="submit"
              disabled={isSaving}
              className="bg-blue-600 hover:bg-blue-700"
            >
              <Save className="w-4 h-4 mr-2" />
              {isSaving ? t('common.saving') : t('common.saveChanges')}
            </Button>
          </div>
        </form>

      </div>

      {deactivateModal.open && (
        <DeactivateUserModal
          open={deactivateModal.open}
          onClose={() => setDeactivateModal({ open: false, dealCount: 0 })}
          userId={userId}
          userName={user?.display_name || [user?.first_name, user?.last_name].filter(Boolean).join(' ') || user?.email || 'Usuário'}
          dealCount={deactivateModal.dealCount}
          onConfirmed={handleDeactivationConfirmed}
        />
      )}
    </div>
  );
}
